import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Button,
  Row,
  Col,
  Typography,
  Menu,
  Upload,
  message,
  Checkbox,
  Modal,
  Space,
  Tooltip,
  Input,
  Dropdown,
  Spin,
  App,
  Drawer,
  Flex,
  Grid,
  Select
} from "antd";
import {
  PlusOutlined,
  DownloadOutlined,
  UploadOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
  SearchOutlined,
  QuestionCircleOutlined
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchItemsList, deleteItem, fetchBranchList } from "../itemManageThunks";
import { setIsEditItem, setSelectedItem } from "../itemManageSlice";
import { getAccessType, Trial_Limits } from "../../../lib/functions";
import { fetchSingleInstitutionDetails } from "../../../store/commonThunks/lookups";

import Papa from "papaparse";
import axios from "axios";
import { BACKEND_API_URL } from "../../../environment";
import ViewItemModal from "./ViewItemModal";
import { useIsAdminUser } from "../../../lib/accessList";

const { Title } = Typography;
const { Option } = Select;
const { confirm } = Modal;
const { useBreakpoint } = Grid;

// ✅ All possible columns
const ALL_COLUMNS = [
  { key: "item_code", label: "Code" },
  { key: "item_name", label: "Product Name" },
  { key: "group_name", label: "Group" },
  { key: "category_name", label: "Category" },
  { key: "unit_name", label: "Unit of Measure" },
  { key: "unit_symbol", label: "Master Unit" },
  { key: "base_unit_name", label: "Base Unit" },
  { key: "sale_rate", label: "Retail Rate / MRP" },       // ← rename label
  { key: "gtn_num", label: "GTN Num" },
  { key: "wholesaler_rate", label: "Class A / Net Selling Rate" }, // ← rename label
  { key: "corporate_rate", label: "Class B Rate" },       // ← ADD
  { key: "distributor_rate", label: "Class C Rate" },     // ← ADD
  { key: "moh_invoice_rate", label: "MOH Invoice Rate" }, // ← ADD
  { key: "net_cost", label: "Net Cost" },                 // ← ADD
  { key: "purchase_rate", label: "Purchase Rate" },
  { key: "opening_rate", label: "Opening Rate" },
  { key: "opening_qty", label: "Opening Qty" },
  { key: "is_serial", label: "Is Serial" },
  { key: "manufacture_name", label: "Manufacture Name" },
  { key: "distributer_name", label: "Distributer Name" },
  { key: "return_days", label: "Return Days" },
];

// ✅ Default visible columns (yesterday’s)
const DEFAULT_VISIBLE_KEYS = [
  "item_code",
  "item_name",
  "group_name",
  "category_name",
  "unit_name",
  "opening_rate",
  "opening_qty",
];

const REQUIRED_COLUMNS = [
  "item_name",
  "group_name",
  "category_name",
  "model",
  "unit",
  "sale_rate",
  "tax_per",
];

const ItemManageList = ({ authInfo }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const { items = [], loading, branches } = useSelector(
    (state) => state.itemManage || {},
  );
  const lookups = useSelector((state) => state.lookups);
  const crossBranchSyncEnabled =
    lookups?.institutionData?.enable_cross_branch_item_sync === "yes";

  const { modal } = App.useApp();
  const [searchText, setSearchText] = useState("");
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedViewItem, setSelectedViewItem] = useState(null);
  const [loadingOverlay, setLoadingOverlay] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // CSV related states
  const [csvFile, setCsvFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [previewFields, setPreviewFields] = useState([]);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [downloadURL, setDownloadURL] = useState(null);
  const [downloadURLModal, setDownloadURLModal] = useState(false);
  const [skippedRows, setSkippedRows] = useState([]);
  const [skippedRowsModal, setSkippedRowsModal] = useState(false);
  const [importSummary, setImportSummary] = useState(null); // { totalRows, importedCount, skippedCount }
const getDefaultVisibleKeys = (authInfo) => {
  const isDrugStoreUAE =
    authInfo?.country === "United Arab Emirates" &&
    authInfo?.company_type === "Drug Store";
if (isDrugStoreUAE) {
  return [
    "item_code",
    "item_name",
    "sale_rate",
    "wholesaler_rate",
    "corporate_rate",
    "distributor_rate",
    "moh_invoice_rate",
    "net_cost",
  ];
}

  return [
    "item_code",
    "item_name",
    "group_name",
    "category_name",
    "unit_name",
    "opening_rate",
    "opening_qty",
  ];
};
const [visibleKeys, setVisibleKeys] = useState(() => getDefaultVisibleKeys(authInfo));

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const [access_type, access_type_set] = useState("");
  const isAdminUser = useIsAdminUser();

  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;

  useEffect(() => {
    const access = getAccessType("item_entry");
    access_type_set(access);
  }, []);

  // Filter columns conditionally
const availableColumns = ALL_COLUMNS.filter((col) => {
  const isDrugStoreUAE =
    authInfo?.country === "United Arab Emirates" &&
    authInfo?.company_type === "Drug Store";

  // GTN only for Drug Store or Pharmacy
  if (col.key === "gtn_num") {
    return (
      authInfo?.company_type === "Drug Store" ||
      authInfo?.company_type === "Pharmacy"
    );
  }

  // These columns only for Drug Store UAE
  if (
    ["corporate_rate", "distributor_rate", "moh_invoice_rate", "net_cost"].includes(col.key)
  ) {
    return isDrugStoreUAE;
  }

  // Rename wholesaler label only for Drug Store UAE — handled via ALL_COLUMNS label
  return true;
});

  useEffect(() => {
    dispatch(setSelectedItem(null));
    dispatch(setIsEditItem(false));
    dispatch(fetchItemsList());
    dispatch(fetchBranchList());
    dispatch(fetchSingleInstitutionDetails());
  }, [dispatch]);

  // CSV branch selection — only used when cross-branch sync is off (when on,
  // every import always spans every branch, matching CreateItemForm.js).
  const [csvBranchList, setCsvBranchList] = useState([]);
  useEffect(() => {
    if (!crossBranchSyncEnabled && authInfo?.userInfo?.branch_id && csvBranchList.length === 0) {
      setCsvBranchList([authInfo.userInfo.branch_id]);
    }
  }, [crossBranchSyncEnabled, authInfo, csvBranchList.length]);

  const handleCsvBranchChange = (values) => {
    if (values.includes("all")) {
      setCsvBranchList((branches || []).map((b) => b.id));
    } else {
      setCsvBranchList(values);
    }
  };

  // Global search
  const filteredItems = useMemo(() => {
    if (!searchText.trim()) return items;
    return items.filter((it) =>
      Object.values(it || {})
        .join(" ")
        .toLowerCase()
        .includes(searchText?.toLowerCase()),
    );
  }, [items, searchText]);



  const handleDownloadTemplate = async () => {
    try {
      setLoadingOverlay(true);
      const response = await fetch(
        `${BACKEND_API_URL}/inventory/product/import-template`,
        {
          method: "GET",
          headers: {
            "auth-token": authInfo?.token,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch template: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Products.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading template:", error);
      message.error("Failed to download template.");
    } finally {
      setLoadingOverlay(false);
    }
  };

  const MAX_FILE_SIZE = 921600; // 900KB

  const handleUploadChange = (info) => {
    const file = info?.file?.originFileObj || info?.file;
    if (!file) return;
    // validate size & type
    if (file.size > MAX_FILE_SIZE) {
      message.warning(
        "File size exceeds 900 KB. Please upload a smaller file.",
      );
      return;
    }
    if (file.type !== "text/csv" && !file.name.toLowerCase().endsWith(".csv")) {
      message.error("Invalid file type. Please upload a CSV file.");
      return;
    }

    // parse file with PapaParse for preview & validations
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        // results.meta.fields contains header fields
        const fields = results.meta.fields || [];
        const missing = REQUIRED_COLUMNS.filter((c) => !fields.includes(c));
        if (missing.length > 0) {
          message.error(
            `Invalid file structure. Missing columns: ${missing.join(", ")}`,
          );
          return;
        }

        // simple row validations (as per old UI)
        const data = results.data || [];
        let isValid = true;
        for (let i = 0; i < data.length; i++) {
          const row = data[i];
          const rowIndex = i + 1;
          if (!row.item_name || String(row.item_name).trim() === "") {
            message.error(`Row ${rowIndex}: Item Name is required`);
            isValid = false;
            break;
          }
          if (!row.unit || String(row.unit).trim() === "") {
            message.error(`Row ${rowIndex}: Unit is required`);
            isValid = false;
            break;
          }
        }

        if (!isValid) return;

        // store for preview
        setCsvFile(file);
        setPreviewData(data);
        setPreviewFields(fields);
        setPreviewModalVisible(true);
      },
      error: (err) => {
        console.error("Papa parse error:", err);
        message.error("Failed to parse CSV file.");
      },
    });
  };

  const handleConfirmImport = async () => {
    if (!csvFile) {
      message.warning("No file selected.");
      return;
    }
    if (!crossBranchSyncEnabled && csvBranchList.length === 0) {
      message.warning("Choose at least one branch to import into.");
      return;
    }
    setIsUploading(true);
    setLoadingOverlay(true);
    try {
      const formData = new FormData();
      formData.append("file", csvFile);
      if (!crossBranchSyncEnabled) {
        formData.append("branch_list", csvBranchList.join(","));
      }

      const res = await axios.post(
        `${BACKEND_API_URL}/inventory/item/upload-csv`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "auth-token": authInfo?.token,
          },
        },
      );

      const data = res?.data;

      // Backend rejections (e.g. trial-limit exceeded) come back as a 200
      // with error:true, not an HTTP error status — axios won't throw for
      // these, so they must be checked explicitly or they'd be silently
      // treated as a successful (empty) import.
      if (data?.error) {
        modal.error({
          title: "Import Failed",
          content: data?.message || "Import could not be completed.",
        });
        return;
      }

      const skippedRows = data?.skippedRows || [];
      const totalRows = data?.totalRows ?? skippedRows.length;
      const skippedCount = data?.skippedCount ?? skippedRows.length;
      const importedCount = data?.importedCount ?? Math.max(0, totalRows - skippedCount);

      setSkippedRows(skippedRows);

      if (data?.downloadUrl) {
        setDownloadURL(`${BACKEND_API_URL}${data.downloadUrl}`);
      } else {
        setDownloadURL(null);
      }

      setPreviewModalVisible(false);
      setCsvFile(null);
      setPreviewData([]);
      setPreviewFields([]);
      dispatch(fetchItemsList());

      // Persistent result modal — a toast is too easy to miss, especially
      // when some rows failed alongside successful ones.
      setImportSummary({ totalRows, importedCount, skippedCount });
      setDownloadURLModal(true);
    } catch (err) {
      console.error("Import failed:", err);
      const msg =
        err?.response?.data?.message || "Invalid file format or structure";
      message.error(msg);
    } finally {
      setIsUploading(false);
      setLoadingOverlay(false);
    }
  };

  const handleDownloadSkipped = async () => {
    if (!downloadURL) {
      message.warn("No skipped file URL available.");
      return;
    }
    try {
      setLoadingOverlay(true);
      const response = await axios.get(downloadURL, {
        headers: {
          "auth-token": authInfo?.token,
        },
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute("download", "skipped_rows.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setDownloadURLModal(false);
      setDownloadURL(null);
    } catch (error) {
      console.error("Error downloading file:", error);
      message.error("Failed to download skipped rows file");
    } finally {
      setLoadingOverlay(false);
    }
  };

  const handleDelete = (record) => {
    confirm({
      title: `Delete ${record.item_name}?`,
      icon: <ExclamationCircleOutlined />,
      content: "This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      async onOk() {
        try {
          await dispatch(deleteItem(record.item_id)).unwrap();
          message.success("Item deleted successfully");
          dispatch(fetchItemsList());
        } catch (err) {
          message.error(err || "Failed to delete item");
        }
      },
    });
  };

  // Toggle visibility
  const handleToggle = (key, checked) => {
    setVisibleKeys((prev) =>
      checked ? [...prev, key] : prev.filter((k) => k !== key),
    );
  };

  const handleExport = (type = "csv") => {
  try {
    const exportData = filteredItems.map((item) => ({
      item_code: item.item_code,
      item_name: item.item_name,
      item_barcode: item.item_barcode,
      group_name: item.group_name,
      category_name: item.category_name,
      model: item.model_name,
      rack: item.rack_name,
      origin: item.origin_name,
      unit: item.unit_name,
      purchase_rate: item.purchase_rate,
      sale_rate: item.sale_rate,
      wholesaler_rate: item.wholesaler_rate,
      retailer_rate: item.retailer_rate,
      corporate_rate: item.corporate_rate,
      distributor_rate: item.distributor_rate,
      tax_per: item.tax_per,
      opening_rate: item.opening_rate,
      opening_qty: item.opening_qty,
      description: item.description,
      manufacture_name: item.manufacture_name,
      distributer_name: item.distributer_name,
    }));

    if (type === "csv") {
      const csv = Papa.unparse(exportData);

      const blob = new Blob([csv], {
        type: "text/csv;charset=utf-8;",
      });

      const link = document.createElement("a");

      link.href = URL.createObjectURL(blob);
      link.download = `items-export-${Date.now()}.csv`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(link.href);
    }

    if (type === "excel") {
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "Products"
      );

      XLSX.writeFile(
        workbook,
        `items-export-${Date.now()}.xlsx`
      );
    }
  } catch (err) {
    console.error(err);
    message.error("Export failed");
  }
};

  // Columns menu with checkboxes
  const columnMenu = (
    <Menu>
      {ALL_COLUMNS.map(({ key, label }) => (
        <Menu.Item key={key}>
          <Checkbox
            checked={visibleKeys.includes(key)}
            onChange={(e) => handleToggle(key, e.target.checked)}
          >
            {label}
          </Checkbox>
        </Menu.Item>
      ))}
    </Menu>
  );

  // Table Columns — only visible ones
  const columns = [
    {
      title: "No.",
      render: (_, __, index) => index + 1,
      width: 60,
      align: "center",
      fixed: "left",
    },

    ...ALL_COLUMNS.filter((col) => {
      // ❌ Hide GTN column unless company type = Drug Store / Pharmacy
      if (
        col.key === "gtn_num" &&
        !(
          authInfo?.company_type === "Drug Store" ||
          authInfo?.company_type === "Pharmacy"
        )
      ) {
        return false;
      }
      // ✅ Only show toggled-on columns
      return visibleKeys.includes(col.key);
    }).map(({ key, label }) => {
 const isNumeric = [
  "opening_rate",
  "opening_qty",
  "sale_rate",
  "purchase_rate",
  "wholesaler_rate",
  "corporate_rate",     // ← ADD
  "distributor_rate",   // ← ADD
  "moh_invoice_rate",   // ← ADD
  "net_cost",           // ← ADD
  "return_days",
  "gtn_num",
].includes(key);

      // 🧮 Build filters only for non-numeric columns
      const shouldHaveFilter = !isNumeric;
      const uniqueValues = shouldHaveFilter
        ? [...new Set(items.map((item) => item[key]).filter(Boolean))]
        : [];

      return {
        title: label,
        dataIndex: key,
        align: isNumeric ? "right" : "left",
        render: (val) =>
          typeof val === "number" && !isNaN(val)
            ? Number(val).toFixed(2)
            : (val ?? ""),
        sorter:
          typeof items[0]?.[key] === "string"
            ? (a, b) => (a[key] || "").localeCompare(b[key] || "")
            : (a, b) => (a[key] || 0) - (b[key] || 0),
        ...(shouldHaveFilter
          ? {
            filters: uniqueValues.map((val) => ({
              text: String(val),
              value: val,
            })),
            onFilter: (value, record) =>
              String(record[key]) === String(value),
          }
          : {}),
      };
    }),

    ...(access_type === "WRITE"
      ? [
        {
          title: "Actions",
          key: "actions",
          align: "center",
          fixed: "right",
          render: (_, record) => (
            <Space>
              <Tooltip title="View Details">
                <Button
                  type="link"
                  icon={<EyeOutlined />}
                  onClick={() => {
                    setSelectedViewItem(record);
                    setViewModalVisible(true);
                  }}
                />
              </Tooltip>

              <Tooltip title="Edit Item">
                <Button
                  type="link"
                  icon={<EditOutlined />}
                  disabled={!(isAdminUser || canEdit)}
                  onClick={() => {
                    dispatch(setSelectedItem(record));
                    dispatch(setIsEditItem(true));
                    navigate("addNewItem");
                  }}
                />
              </Tooltip>

              <Tooltip title="Delete Item">
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={!(isAdminUser || canDelete)}
                  onClick={() => handleDelete(record)}
                />
              </Tooltip>
            
            
            </Space >
          ),
        },
      ]
      : []),
  ];

// Build preview table columns (from previewFields)
const buildPreviewColumns = () => {
  if (!previewFields || previewFields.length === 0) return [];
  return previewFields.map((f) => ({
    title: f,
    dataIndex: f,
    key: f,
    render: (text) =>
      text === null || text === undefined ? "" : String(text),
  }));
};

const LoaderOverlay = () =>
  loadingOverlay ? (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(255, 255, 255, 0.7)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
      }}
    >
      <Spin size="large" />
    </div>
  ) : null;

// Small helper component: Column visibility dropdown

const ColumnVisibilityDropdown = ({
  allColumns,
  visibleKeys,
  setVisibleKeys,
}) => {
  const items = allColumns.map((c) => ({
    key: c.key,
    label: (
      <Checkbox
        checked={visibleKeys.includes(c.key)}
        onChange={(e) => {
          const checked = e.target.checked;
          setVisibleKeys((prev) =>
            checked ? [...prev, c.key] : prev.filter((k) => k !== c.key),
          );
        }}
      >
        {c.label}
      </Checkbox>
    ),
  }));

  return (
    <Dropdown overlay={<Menu items={items} />} placement="bottomRight">
      <Button style={{ marginLeft: 8 }}>Columns</Button>
    </Dropdown>
  );
};

return (
  <>
    <LoaderOverlay />
    <Row
      justify="space-between"
      align="middle"
      gutter={[16, 16]}
      style={{ marginTop: 24, marginBottom: 24 }}
    >
      {/* Title on the left */}
      {/* <Col xs={12} sm={12}> */}
      <Col>
        <Title
          level={4}
          style={{
            margin: 0,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",

          }}
        >
          Item List
        </Title>
      </Col>

      {/* Right side buttons */}
      <Col
        xs={24}
        sm={12}
        md={8}
        lg={16}
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "1rem",
        }}
      >
        {access_type === "WRITE" && (
          <>
            <Input
              placeholder="Search..."
              prefix={<SearchOutlined />}
              allowClear
              style={{ width: screens.sm ? 250 : "100%" }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />

            <ColumnVisibilityDropdown
              allColumns={availableColumns}
              visibleKeys={visibleKeys}
              setVisibleKeys={setVisibleKeys}
            />

            <Tooltip title="Export">
              <Dropdown
                menu={{
                  items: [
                    {
                      key: "csv",
                      label: "Export CSV",
                      onClick: () => handleExport("csv"),
                    },
                    {
                      key: "excel",
                      label: "Export Excel",
                      onClick: () => handleExport("excel"),
                    },
                  ],
                }}
                placement="bottomRight"
                arrow
              >
                <Button icon={<DownloadOutlined />}>Export</Button>
              </Dropdown>
            </Tooltip>

            <Tooltip title="Reset Search">
              <Button
                icon={<ReloadOutlined />}
                onClick={() => setSearchText("")}
              >
                Reset
              </Button>
            </Tooltip>

            {/* Upload CSV */}
            <Upload
              accept=".csv"
              showUploadList={false}
              beforeUpload={() => false}
              onChange={handleUploadChange}
            >
              <Button icon={<UploadOutlined />}>Import CSV</Button>
            </Upload>

            {/* Download Template */}
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadTemplate}
            >
              Download Template
            </Button>
          </>
        )}

        <Flex align="center" gap={6}>
          <Tooltip title="Help">
            <Button
              type="text"
              icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
              onClick={() => setHelpOpen(true)}
            />
          </Tooltip>

          {access_type === "WRITE" && (
            <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {

              const trialMode = authInfo?.trial_mode;
              const userName = authInfo?.userInfo?.name || "User";
              if (trialMode == "yes" && items.length >= Trial_Limits.product_count) {
                modal.info({
                  title: "Free Limit Reached",
                  content:
                    `Hi ${userName}, you are currently in Free mode. You can create up to 10 products during the Free Version.`,
                });
                return;
              }


              dispatch(setSelectedItem(null));
              dispatch(setIsEditItem(false));
              navigate("/inventory/product/addNewItem");
            }}
          >
            Add New
          </Button>
        )}
        </Flex>
      </Col>
    </Row>

    {/* Table */}
    <div
      style={{
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <Table
        bordered
        columns={columns}
        dataSource={filteredItems}
        loading={loading}
        rowKey="item_id"
        size="small"
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
        }}
        onChange={handleTableChange}
        scroll={{ x: "max-content" }}
        rowClassName={(_, index) =>
          index % 2 === 0 ? "table-row-light" : "table-row-dark"
        }
        style={{
          backgroundColor: "#fff",
          borderRadius: 8,
        }}
      />
    </div>

    <ViewItemModal
      visible={viewModalVisible}
      onClose={() => setViewModalVisible(false)}
      item={selectedViewItem}
    />

    {/* Preview Modal */}
    <Modal
      title="Preview CSV Data"
      // visible={previewModalVisible}
      open={previewModalVisible}
      onOk={handleConfirmImport}
      onCancel={() => {
        setPreviewModalVisible(false);
        setCsvFile(null);
        setPreviewData([]);
        setPreviewFields([]);
      }}
      okText={isUploading ? "Importing..." : "Import"}
      okButtonProps={{ loading: isUploading }}
      width={900}
    >
      {crossBranchSyncEnabled ? (
        <div style={{ marginBottom: 16 }}>
          This import will create every item in all {(branches || []).length} branches.
        </div>
      ) : (
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 4 }}>Import into branch(es)</div>
          <Select
            mode="multiple"
            placeholder="Choose Branch"
            style={{ width: "100%" }}
            value={csvBranchList}
            onChange={handleCsvBranchChange}
            maxTagCount="responsive"
          >
            <Option value="all">All</Option>
            {(branches || []).map((b) => (
              <Option key={b.id} value={b.id}>
                {b.name}
              </Option>
            ))}
          </Select>
        </div>
      )}
      {previewData && previewData.length > 0 ? (
        <Table
          dataSource={previewData.map((r, idx) => ({ ...r, key: idx }))}
          columns={buildPreviewColumns()}
          size="small"
          pagination={{ pageSize: 5 }}
          scroll={{ x: true }}
        />
      ) : (
        <div>No preview data</div>
      )}
    </Modal>

    {/* Import result summary — a persistent modal so the outcome can't be missed like a toast can */}
    <Modal
      title="Import Result"
      open={downloadURLModal}
      onCancel={() => {
        setDownloadURLModal(false);
        setImportSummary(null);
      }}
      footer={[
        importSummary?.skippedCount > 0 ? (
          <Button
            key="view-skipped"
            onClick={() => setSkippedRowsModal(true)}
          >
            View Skipped Rows
          </Button>
        ) : null,
        downloadURL ? (
          <Button
            key="download"
            onClick={handleDownloadSkipped}
            icon={<DownloadOutlined />}
          >
            Download Skipped Rows
          </Button>
        ) : null,
        <Button
          key="close"
          type="primary"
          onClick={() => {
            setDownloadURLModal(false);
            setImportSummary(null);
          }}
        >
          Close
        </Button>,
      ]}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <span style={{ color: "#389e0d", fontWeight: 500 }}>
          ✅ {importSummary?.importedCount ?? 0} of {importSummary?.totalRows ?? 0} item(s) imported successfully.
        </span>
        {importSummary?.skippedCount > 0 && (
          <span style={{ color: "#cf1322", fontWeight: 500 }}>
            ⚠️ {importSummary.skippedCount} row(s) skipped — see "View Skipped Rows" or download the CSV for details.
          </span>
        )}
      </div>
    </Modal>

    <Modal
      open={skippedRowsModal}
      onCancel={() => setSkippedRowsModal(false)}
      footer={null}
      width={"70%"}
    >
      <h2>Skipped Rows Preview</h2>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          paddingBottom: "10px",
        }}
      >
        <span>Total Skipped: {skippedRows.length}</span>

        <Button
          type="primary"
          onClick={async () => {
            try {
              const response = await axios.get(downloadURL, {
                headers: { "auth-token": authInfo.token },
                responseType: "blob",
              });

              const blob = new Blob([response.data], { type: "text/csv" });
              const link = document.createElement("a");
              link.href = window.URL.createObjectURL(blob);
              link.setAttribute("download", "skipped_rows.csv");
              document.body.appendChild(link);
              link.click();
              link.remove();

              setSkippedRowsModal(false);
            } catch {
              message.error("Failed to download skipped rows.");
            }
          }}
        >
          Download Skipped Rows
        </Button>
      </div>

      <div
        style={{
          maxHeight: "70vh",
          overflowY: "auto",
          overflowX: "auto",
          border: "1px solid #ddd",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {Object.keys(skippedRows[0] || {}).map((key) => (
                <th
                  key={key}
                  style={{
                    padding: 10,
                    background: "#f0f0f0",
                    border: "1px solid #ddd",
                  }}
                >
                  {key}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {skippedRows.map((row, index) => (
              <tr key={index}>
                {Object.values(row).map((value, idx) => (
                  <td
                    key={idx}
                    style={{
                      padding: 10,
                      border: "1px solid #ddd",
                    }}
                  >
                    {value}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
    <Drawer
      title="Product Management Help"
      open={helpOpen}
      onClose={() => setHelpOpen(false)}
      width={460}
      styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
    >
      <div style={{ lineHeight: 1.8 }}>
        <h2
          style={{
            fontSize: "18px",
            fontWeight: 600,
            marginBottom: "12px",
            marginLeft: "12px"
          }}
        >
          Product Management
        </h2>

        <p style={{ color: "#555", marginBottom: "16px", marginLeft:17 }}>
          This section helps manage products, pricing, stock opening details,
          packaging information, import/export operations, and inventory product workflows.
        </p>

        <div
          style={{
            background: "#f5f5f5",
            padding: "14px",
            borderRadius: "8px",
            marginBottom: "16px",
          }}
        >
          <strong>What You Can Do</strong>

          <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
            <li>Create and manage product records</li>
            <li>Edit product pricing and stock details</li>
            <li>Manage product categories, groups, and units</li>
            <li>Track opening quantities and opening rates</li>
            <li>Manage retail, wholesale, and other selling rates</li>
            <li>Search products instantly using global search</li>
            <li>View detailed product information</li>
            <li>Import products using CSV files</li>
            <li>Download CSV templates for bulk uploads</li>
            <li>Export product lists to CSV or Excel</li>
            <li>Customize visible table columns</li>
            <li>Manage GTN numbers and product return days</li>
            <li>Track manufacturer and distributor details</li>
            <li>Delete products if permitted</li>
          </ol>
        </div>

        <div
          style={{
            background: "#fafafa",
            border: "1px solid #eee",
            padding: "14px",
            borderRadius: "8px",
          }}
        >
          <strong>Important Notes</strong>

          <ul
            style={{
              paddingLeft: "18px",
              marginTop: "10px",
              color: "#666",
            }}
          >
            <li>CSV imports must follow the provided template structure.</li>
            <li>Required columns are mandatory during CSV import.</li>
            <li>Skipped import rows can be downloaded and reviewed later.</li>
            <li>Product pricing affects sales and inventory calculations.</li>
            <li>Column visibility settings help simplify product management.</li>
            <li>Only authorized users can edit or delete products.</li>
            <li>GTN fields appear only for supported company types.</li>
            <li>Bulk imports help speed up product creation workflows.</li>
          </ul>
        </div>
      </div>
    </Drawer>
  </>
);
};

export default ItemManageList;
